/*
Audit Dashboard Component
Provides overview and analytics for vehicle audit logs
Author: System
Date: 2025-01-27
*/

import React, { useState, useEffect } from 'react'
import { Card, Text, Flex, Badge, Button, Select, TextField, Grid, Callout, Separator } from '@radix-ui/themes'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { ActivityIcon, TrendingUpIcon, UsersIcon, CarIcon, CalendarIcon, SearchIcon } from 'lucide-react'
import { format, subDays } from 'date-fns'
import { es } from 'date-fns/locale'

import { useVehicleAudit } from '@/hooks/useVehicleAudit'
import { useAuth } from '@/hooks/useAuth'
import { UserProfileRole } from '@/modules/users/types/UserProfile'

const actionColors: Record<string, string> = {
  create: '#10B981',
  update: '#3B82F6',
  delete: '#EF4444',
  archive: '#F59E0B',
  restore: '#10B981',
  publish: '#8B5CF6',
  unpublish: '#6B7280',
}

interface AuditDashboardProps {
  className?: string
}

export const AuditDashboard: React.FC<AuditDashboardProps> = ({ className }) => {
  const [stats, setStats] = useState<any>(null)
  const [summary, setSummary] = useState<any>(null)
  const [searchResults, setSearchResults] = useState<any>(null)
  const [dateRange, setDateRange] = useState('30')
  const [searchParams, setSearchParams] = useState({
    action: '',
    userEmail: '',
    vehicleId: '',
  })

  const { loading, error, fetchAuditStats, fetchAuditSummary, searchAuditLogs } = useVehicleAudit()
  const { hasRole } = useAuth()

  useEffect(() => {
    loadInitialData()
  }, [dateRange])

  const loadInitialData = async () => {
    try {
      const days = parseInt(dateRange, 10)
      const endDate = new Date()
      const startDate = subDays(endDate, days)

      const [statsData, summaryData] = await Promise.all([
        fetchAuditStats(),
        fetchAuditSummary(format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd')),
      ])

      setStats(statsData)
      setSummary(summaryData)
    } catch (err) {
      console.error('Failed to load audit data:', err)
    }
  }

  const handleSearch = async () => {
    try {
      const results = await searchAuditLogs({
        ...searchParams,
        limit: 50,
      })
      setSearchResults(results)
    } catch (err) {
      console.error('Search failed:', err)
    }
  }

  const renderStatsCards = () => {
    if (!stats) return null

    return (
      <Grid columns={{ initial: '2', sm: '4' }} gap="3">
        <Card>
          <Flex direction="column" gap="2">
            <Flex align="center" gap="2">
              <ActivityIcon size={20} color="#3B82F6" />
              <Text size="2" color="gray">
                Total Auditorías
              </Text>
            </Flex>
            <Text size="6" weight="bold">
              {stats.totalAudits.toLocaleString()}
            </Text>
          </Flex>
        </Card>

        <Card>
          <Flex direction="column" gap="2">
            <Flex align="center" gap="2">
              <TrendingUpIcon size={20} color="#10B981" />
              <Text size="2" color="gray">
                Últimos 30 días
              </Text>
            </Flex>
            <Text size="6" weight="bold">
              {stats.auditsLast30Days.toLocaleString()}
            </Text>
          </Flex>
        </Card>

        <Card>
          <Flex direction="column" gap="2">
            <Flex align="center" gap="2">
              <UsersIcon size={20} color="#F59E0B" />
              <Text size="2" color="gray">
                Usuarios Activos
              </Text>
            </Flex>
            <Text size="6" weight="bold">
              {summary?.activeUsers?.length || 0}
            </Text>
          </Flex>
        </Card>

        <Card>
          <Flex direction="column" gap="2">
            <Flex align="center" gap="2">
              <CarIcon size={20} color="#8B5CF6" />
              <Text size="2" color="gray">
                Vehículos Modificados
              </Text>
            </Flex>
            <Text size="6" weight="bold">
              {summary?.mostChangedVehicles?.length || 0}
            </Text>
          </Flex>
        </Card>
      </Grid>
    )
  }

  const renderActionBreakdown = () => {
    if (!stats?.actionBreakdown) return null

    const data = stats.actionBreakdown.map((item: any) => ({
      action: item._id,
      count: item.count,
      fill: actionColors[item._id] || '#6B7280',
    }))

    return (
      <Card>
        <Flex direction="column" gap="3">
          <Text size="4" weight="bold">
            Distribución por Tipo de Acción
          </Text>
          <div style={{ width: '100%', height: '300px' }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ action, percent }) => `${action} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {data.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Flex>
      </Card>
    )
  }

  const renderDailyActivity = () => {
    if (!stats?.dailyActivity) return null

    return (
      <Card>
        <Flex direction="column" gap="3">
          <Text size="4" weight="bold">
            Actividad Diaria
          </Text>
          <div style={{ width: '100%', height: '300px' }}>
            <ResponsiveContainer>
              <LineChart data={stats.dailyActivity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(value) => format(new Date(value), 'dd/MM', { locale: es })} />
                <YAxis />
                <Tooltip labelFormatter={(value) => format(new Date(value), 'dd MMMM yyyy', { locale: es })} />
                <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} name="Cambios" />
                <Line type="monotone" dataKey="uniqueUsers" stroke="#10B981" strokeWidth={2} name="Usuarios Únicos" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Flex>
      </Card>
    )
  }

  const renderActiveUsers = () => {
    if (!summary?.activeUsers) return null

    return (
      <Card>
        <Flex direction="column" gap="3">
          <Text size="4" weight="bold">
            Usuarios Más Activos
          </Text>
          <Flex direction="column" gap="2">
            {summary.activeUsers.slice(0, 5).map((user: any, index: number) => (
              <Flex
                key={user._id}
                justify="between"
                align="center"
                p="2"
                style={{ backgroundColor: 'var(--gray-2)', borderRadius: '6px' }}
              >
                <Flex align="center" gap="2">
                  <Badge color="blue" size="1">
                    #{index + 1}
                  </Badge>
                  <Text size="2">{user._id}</Text>
                </Flex>
                <Flex align="center" gap="2">
                  <Text size="2" color="gray">
                    {user.totalChanges} cambios
                  </Text>
                  <Text size="1" color="gray">
                    {format(new Date(user.lastActivity), 'dd/MM', { locale: es })}
                  </Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Card>
    )
  }

  const renderMostChangedVehicles = () => {
    if (!summary?.mostChangedVehicles) return null

    return (
      <Card>
        <Flex direction="column" gap="3">
          <Text size="4" weight="bold">
            Vehículos Más Modificados
          </Text>
          <Flex direction="column" gap="2">
            {summary.mostChangedVehicles.slice(0, 5).map((vehicle: any, index: number) => (
              <Flex
                key={vehicle.vehicleId}
                justify="between"
                align="center"
                p="2"
                style={{ backgroundColor: 'var(--gray-2)', borderRadius: '6px' }}
              >
                <Flex direction="column" gap="1">
                  <Flex align="center" gap="2">
                    <Badge color="purple" size="1">
                      #{index + 1}
                    </Badge>
                    <Text size="2" weight="medium">
                      {vehicle.plate} - {vehicle.brand} {vehicle.model}
                    </Text>
                  </Flex>
                  <Text size="1" color="gray">
                    Año {vehicle.year}
                  </Text>
                </Flex>
                <Flex align="center" gap="2">
                  <Text size="2" color="gray">
                    {vehicle.totalChanges} cambios
                  </Text>
                  <Text size="1" color="gray">
                    {vehicle.users.length} usuario{vehicle.users.length !== 1 ? 's' : ''}
                  </Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Card>
    )
  }

  const renderSearchSection = () => {
    return (
      <Card>
        <Flex direction="column" gap="3">
          <Text size="4" weight="bold">
            Buscar en Auditorías
          </Text>

          <Grid columns={{ initial: '1', sm: '3' }} gap="3">
            <Select.Root
              value={searchParams.action}
              onValueChange={(value) => setSearchParams((prev) => ({ ...prev, action: value }))}
            >
              <Select.Trigger placeholder="Tipo de acción" />
              <Select.Content>
                <Select.Item value="">Todas las acciones</Select.Item>
                <Select.Item value="create">Creación</Select.Item>
                <Select.Item value="update">Modificación</Select.Item>
                <Select.Item value="archive">Archivo</Select.Item>
                <Select.Item value="restore">Restauración</Select.Item>
                <Select.Item value="publish">Publicación</Select.Item>
                <Select.Item value="unpublish">Despublicación</Select.Item>
              </Select.Content>
            </Select.Root>

            <TextField.Root
              placeholder="Email del usuario"
              value={searchParams.userEmail}
              onChange={(e) => setSearchParams((prev) => ({ ...prev, userEmail: e.target.value }))}
            />

            <TextField.Root
              placeholder="ID del vehículo"
              value={searchParams.vehicleId}
              onChange={(e) => setSearchParams((prev) => ({ ...prev, vehicleId: e.target.value }))}
            />
          </Grid>

          <Button onClick={handleSearch} disabled={loading}>
            <SearchIcon size={16} />
            Buscar
          </Button>

          {searchResults && (
            <div>
              <Separator my="3" />
              <Text size="3" weight="medium" mb="2">
                Resultados: {searchResults.results.length} de {searchResults.pagination.total}
              </Text>
              <Flex direction="column" gap="2">
                {searchResults.results.map((result: any) => (
                  <Card key={result._id} variant="surface">
                    <Flex justify="between" align="start">
                      <Flex direction="column" gap="1">
                        <Flex align="center" gap="2">
                          <Badge color={actionColors[result.action] ? 'blue' : 'gray'} size="1">
                            {result.action}
                          </Badge>
                          <Text size="2">{result.userEmail}</Text>
                          <Text size="1" color="gray">
                            {format(new Date(result.timestamp), 'dd/MM/yyyy HH:mm', { locale: es })}
                          </Text>
                        </Flex>
                        {result.vehicle && (
                          <Text size="2" color="gray">
                            {result.vehicle.plate} - {result.vehicle.brand} {result.vehicle.model} (
                            {result.vehicle.year})
                          </Text>
                        )}
                        {result.changesSummary && result.changesSummary.length > 0 && (
                          <Text size="1" color="gray">
                            {result.changesSummary.slice(0, 2).join(', ')}
                            {result.changesSummary.length > 2 && '...'}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  </Card>
                ))}
              </Flex>
            </div>
          )}
        </Flex>
      </Card>
    )
  }

  if (!hasRole(UserProfileRole.ViewProfiles)) {
    return (
      <Callout.Root color="red">
        <Callout.Text>No tienes permisos para ver el dashboard de auditoría.</Callout.Text>
      </Callout.Root>
    )
  }

  return (
    <div className={className}>
      <Flex direction="column" gap="4">
        {/* Header */}
        <Flex justify="between" align="center">
          <Flex direction="column" gap="1">
            <Text size="6" weight="bold">
              Dashboard de Auditoría
            </Text>
            <Text size="3" color="gray">
              Monitoreo de cambios en vehículos y actividad del sistema
            </Text>
          </Flex>

          <Flex align="center" gap="2">
            <CalendarIcon size={16} />
            <Select.Root value={dateRange} onValueChange={setDateRange}>
              <Select.Trigger />
              <Select.Content>
                <Select.Item value="7">Últimos 7 días</Select.Item>
                <Select.Item value="30">Últimos 30 días</Select.Item>
                <Select.Item value="90">Últimos 90 días</Select.Item>
              </Select.Content>
            </Select.Root>
          </Flex>
        </Flex>

        {error && (
          <Callout.Root color="red">
            <Callout.Text>{error}</Callout.Text>
          </Callout.Root>
        )}

        {/* Stats Cards */}
        {renderStatsCards()}

        {/* Charts */}
        <Grid columns={{ initial: '1', lg: '2' }} gap="4">
          {renderActionBreakdown()}
          {renderDailyActivity()}
        </Grid>

        {/* Lists */}
        <Grid columns={{ initial: '1', lg: '2' }} gap="4">
          {renderActiveUsers()}
          {renderMostChangedVehicles()}
        </Grid>

        {/* Search */}
        {renderSearchSection()}
      </Flex>
    </div>
  )
}

export default AuditDashboard
